// src/lib/lever-metrics-mapper.ts
// PRIO 2 FIX: Maps levers to metrics based on DIMENSION, not index

import type { DiagnosticDimension } from "./master-diagnostic-system";

export type LeverMetric = {
  currentMetric: number;
  potentialMetric: number;
  metricLabel: string;
  quickWinLevel: "Quick Win" | "Mittel" | "Langfristig";
  icon: "activity" | "zap" | "target" | "users" | "globe" | "trending-up";
};

/**
 * Maps a lever to its appropriate metric based on the weakest dimension
 * 
 * This replaces the old index-based mapping (index 0 → Discovery, index 1 → Contact, etc.)
 * with a dimension-based mapping that's causally correct.
 */
export function getLeverMetrics(
  dimension: DiagnosticDimension | undefined,
  leverTitle: string,
  computedMetrics: {
    convContactToDiscovery: number;
    benchmarkContactToDiscovery: number;
    convDiscoveryToPresentation: number;
    benchmarkDiscoveryToPresentation: number;
    convPresentationToDeal: number;
    benchmarkPresentationToDeal: number;
  }
): LeverMetric {
  // Default fallback
  let metric: LeverMetric = {
    currentMetric: 0,
    potentialMetric: 0,
    metricLabel: "Conversion-Rate",
    quickWinLevel: "Mittel",
    icon: "activity",
  };

  // Map based on DIMENSION (not index!)
  switch (dimension) {
    case "DEMAND":
      // Demand issues affect the TOP of funnel
      metric = {
        currentMetric: computedMetrics.convContactToDiscovery,
        potentialMetric: computedMetrics.benchmarkContactToDiscovery,
        metricLabel: "Kontakt → Discovery",
        quickWinLevel: "Quick Win",
        icon: "activity",
      };
      break;

    case "WEBSITE":
      // Inbound qualification affects early funnel
      metric = {
        currentMetric: computedMetrics.convContactToDiscovery,
        potentialMetric: computedMetrics.benchmarkContactToDiscovery,
        metricLabel: "Kontakt → Discovery",
        quickWinLevel: "Quick Win",
        icon: "zap",
      };
      break;

    case "CONVERSION":
      // Conversion issues affect the MIDDLE of funnel
      metric = {
        currentMetric: computedMetrics.convDiscoveryToPresentation,
        potentialMetric: computedMetrics.benchmarkDiscoveryToPresentation,
        metricLabel: "Discovery → Präsentation",
        quickWinLevel: "Mittel",
        icon: "zap",
      };
      break;

    case "SALES_QUALITY":
      // Sales quality issues affect discovery and qualification
      metric = {
        currentMetric: computedMetrics.convDiscoveryToPresentation,
        potentialMetric: computedMetrics.benchmarkDiscoveryToPresentation,
        metricLabel: "Discovery → Präsentation",
        quickWinLevel: "Mittel",
        icon: "users",
      };
      break;

    case "PROCESS":
      // Process issues affect the BOTTOM of funnel (closing)
      metric = {
        currentMetric: computedMetrics.convPresentationToDeal,
        potentialMetric: computedMetrics.benchmarkPresentationToDeal,
        metricLabel: "Präsentation → Deal",
        quickWinLevel: "Langfristig",
        icon: "target",
      };
      break;

    case "POSITIONING":
      // Positioning issues affect overall conversion
      metric = {
        currentMetric: computedMetrics.convPresentationToDeal,
        potentialMetric: computedMetrics.benchmarkPresentationToDeal,
        metricLabel: "Präsentation → Deal",
        quickWinLevel: "Langfristig",
        icon: "trending-up",
      };
      break;

    case "REP_PERFORMANCE":
      // Rep performance is about execution across all stages
      metric = {
        currentMetric: computedMetrics.convDiscoveryToPresentation,
        potentialMetric: computedMetrics.benchmarkDiscoveryToPresentation,
        metricLabel: "Team-Performance",
        quickWinLevel: "Mittel",
        icon: "users",
      };
      break;

    default:
      // Fallback: Use middle of funnel
      metric = {
        currentMetric: computedMetrics.convDiscoveryToPresentation,
        potentialMetric: computedMetrics.benchmarkDiscoveryToPresentation,
        metricLabel: "Discovery → Präsentation",
        quickWinLevel: "Mittel",
        icon: "activity",
      };
  }

  return metric;
}

/**
 * Alternative: Infer dimension from lever title
 * (Fallback if dimension is not directly available)
 */
export function inferDimensionFromTitle(title: string): DiagnosticDimension | undefined {
  const titleLower = title.toLowerCase();

  if (titleLower.includes("lead") || titleLower.includes("nachfrage") || titleLower.includes("qualität")) {
    return "DEMAND";
  }
  if (titleLower.includes("website") || titleLower.includes("vertrauen") || titleLower.includes("online")) {
    return "WEBSITE";
  }
  if (titleLower.includes("conversion") || titleLower.includes("funnel") || titleLower.includes("rate")) {
    return "CONVERSION";
  }
  if (titleLower.includes("discovery") || titleLower.includes("qualifizierung") || titleLower.includes("gespräch")) {
    return "SALES_QUALITY";
  }
  if (titleLower.includes("prozess") || titleLower.includes("crm") || titleLower.includes("pipeline") || titleLower.includes("follow-up")) {
    return "PROCESS";
  }
  if (titleLower.includes("positionierung") || titleLower.includes("differenzierung") || titleLower.includes("proof")) {
    return "POSITIONING";
  }
  if (titleLower.includes("rep") || titleLower.includes("team") || titleLower.includes("performer") || titleLower.includes("zielerreichung")) {
    return "REP_PERFORMANCE";
  }

  return undefined;
}
